import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Printer, List } from "@phosphor-icons/react";

/**
 * <LegalDocShell> — canonical wrapper for every NaturePulse legal document.
 *
 * Renders:
 *  - a header (eyebrow / title / effective + updated dates / print button)
 *  - a sticky TOC sidebar (desktop) / floating drawer (mobile)
 *  - the body sections (each wrapped in <section id={s.id}>) with anchor
 *    bookmarks so the Footer can deep-link to any clause
 *  - a "See other legal notices" nav at the bottom
 *
 * Props:
 *   eyebrow    string      e.g. "NaturePulse · Terms of Service"
 *   title      string      e.g. "Terms of Service"
 *   effective  string      "August 24, 2026"
 *   updated    string      "August 24, 2026"
 *   intro      ReactNode?  paragraph(s) shown above the TOC
 *   sections   Array<{ id: string, title: string, body: ReactNode }>
 */
export default function LegalDocShell({
  eyebrow, title, effective, updated, intro, sections = [],
}) {
  const { hash } = useLocation();
  const [tocOpen, setTocOpen] = useState(false);

  // Scroll to the hash target once the doc has mounted (react-router doesn't
  // auto-scroll to anchor targets, and our ScrollToTop resets to 0).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;
    // wait one paint so DOM is ready
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [hash]);

  const items = useMemo(
    () => sections.map((s, i) => ({ ...s, num: i + 1 })),
    [sections],
  );

  return (
    <div className="min-h-screen pt-28 pb-24 px-4 md:px-8 text-[color:var(--np-text)]">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-10 lg:gap-16">

        {/* ─────────── main column ─────────── */}
        <article className="max-w-[760px] mx-auto lg:mx-0 w-full">
          <Link
            to="/legal"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)] hover:text-[color:var(--np-brand)] mb-6"
          >
            <ArrowLeft size={12} weight="bold" /> All legal notices
          </Link>

          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand)]">
            {eyebrow}
          </div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            {title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted)]">
            {effective && <span data-testid="legal-effective">Effective · {effective}</span>}
            {updated && <span data-testid="legal-updated">Updated · {updated}</span>}
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 hover:text-[color:var(--np-brand)] print:hidden"
              data-testid="legal-print-btn"
            >
              <Printer size={12} weight="bold" /> Print
            </button>
            <button
              type="button"
              onClick={() => setTocOpen(true)}
              className="inline-flex items-center gap-1.5 lg:hidden hover:text-[color:var(--np-brand)]"
              data-testid="legal-toc-open"
            >
              <List size={12} weight="bold" /> Contents
            </button>
          </div>

          {intro && (
            <div className="mt-8 text-[color:var(--np-text-2)] leading-relaxed space-y-4 text-[15px]">
              {intro}
            </div>
          )}

          {/* Body */}
          <div className="mt-14 space-y-14">
            {items.map((s) => (
              <section
                key={s.id}
                id={s.id}
                data-testid={`legal-section-${s.id}`}
                className="scroll-mt-32"
              >
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand)]">
                  § {String(s.num).padStart(2, "0")}
                </div>
                <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight tracking-tight text-[color:var(--np-text)]">
                  {s.title}
                </h2>
                <div className="mt-4 text-[color:var(--np-text-2)] leading-relaxed space-y-4 text-[15px] legal-prose">
                  {s.body}
                </div>
              </section>
            ))}
          </div>
        </article>

        {/* ─────────── TOC sidebar (desktop) ─────────── */}
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted)] mb-4">
              / Contents
            </div>
            <nav className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-2 space-y-1.5">
              {items.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`legal-toc-${s.id}`}
                  className={`block text-[13px] leading-snug py-1 pl-3 border-l transition-colors ${
                    hash === `#${s.id}`
                      ? "border-[color:var(--np-brand)] text-[color:var(--np-brand)]"
                      : "border-black/10 hover:border-[color:var(--np-brand)] hover:text-[color:var(--np-brand)] text-[color:var(--np-text-2)]"
                  }`}
                >
                  <span className="font-mono-tech text-[9px] tracking-[0.24em] text-[color:var(--np-muted)] mr-2">
                    {String(s.num).padStart(2, "0")}
                  </span>
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* ─────────── TOC drawer (mobile) ─────────── */}
        {tocOpen && (
          <div
            className="lg:hidden fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
            onClick={() => setTocOpen(false)}
          >
            <div
              className="absolute right-0 top-0 bottom-0 w-[86%] max-w-sm bg-[color:var(--np-bg)] shadow-2xl p-6 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand)]">
                  Contents
                </div>
                <button
                  onClick={() => setTocOpen(false)}
                  className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]"
                  data-testid="legal-toc-close"
                >
                  Close
                </button>
              </div>
              <nav className="space-y-2">
                {items.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={() => setTocOpen(false)}
                    className="block text-[14px] py-1.5 pl-3 border-l border-black/10 hover:border-[color:var(--np-brand)] hover:text-[color:var(--np-brand)] text-[color:var(--np-text-2)]"
                  >
                    <span className="font-mono-tech text-[9px] tracking-[0.24em] text-[color:var(--np-muted)] mr-2">
                      {String(s.num).padStart(2, "0")}
                    </span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* light typographic tuning for legal-prose blocks */}
      <style>{`
        .legal-prose ul { list-style: disc; padding-left: 1.25rem; }
        .legal-prose ul li { margin: 0.25rem 0; }
        .legal-prose h3 { font-family: var(--font-serif-display, ui-serif, Georgia, serif); font-size: 1.15rem; margin-top: 1.25rem; color: var(--np-text); }
        .legal-prose a { color: var(--np-brand); text-decoration: underline; }
        @media print {
          aside, [data-testid="legal-toc-open"], [data-testid="legal-print-btn"], [data-testid="legal-back-link"] { display: none !important; }
          article { max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
